import { Component, inject, Input, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, Observable, of, tap } from 'rxjs';

import { environment } from '$env';
import { NotificationService } from '$services';
import { AuthorizenetConfig, CardData, DispatchDataResponse } from 'src/app/components/authorize-net/authorize-net';
import { AuthorizenetComponent } from 'src/app/components/authorize-net/authorize-net.component';
import { ModalWizardModels } from 'src/app/components/modal-wizard/shared/models/modal-wizard.model';
import { AuthorizenetComponent as AuthorizenetComponent_1 } from '../../../../authorize-net/authorize-net.component';
import { BidDepositComponent } from '../../components/bid-deposit/bid-deposit.component';
import { BiddingStoreKey, BiddingStoreService } from '../../store/bidding.store';

@UntilDestroy()
@Component({
  selector: 'app-bid-deposit-step-content',
  template: `
    <div class="app-bid-deposit-step-container">
      <app-authorizenet [isDevMode]="!isProductionMode" [config]="authorizenetConfig" />
      <app-bid-deposit (formReady)="onFormReady($event)" (valuesChanged)="onValuesChanged($event)" />
    </div>
  `,

  imports: [AuthorizenetComponent_1, BidDepositComponent],
})
export class BidDepositStepContentComponent implements ModalWizardModels.StepContent {
  biddingStore = inject(BiddingStoreService);
  private notification = inject(NotificationService);
  @Input() stepId = BiddingStoreKey.bidDeposit;

  @ViewChild(AuthorizenetComponent, { static: true }) private authorizenetComponent!: AuthorizenetComponent;

  readonly isProductionMode = environment.production;

  authorizenetConfig: AuthorizenetConfig = {
    apiLoginID: environment.licenses.authorizeNet.apiLoginId,
    clientKey: environment.licenses.authorizeNet.clientKey,
  };

  private form: UntypedFormGroup | null = null;
  private values: { [key: string]: any } = {};

  onFormReady(form: UntypedFormGroup) {
    this.form = form;
  }

  onValuesChanged(values: any): void {
    this.values = values;
    /**
     * Remove all dashes from card number to avoid exception (max length
     * validation fails) after passing data into Authorize.net "dispatchData"
     * method.
     */
    const cardNumber = (this.values['cardNumber'] && this.values['cardNumber'].replaceAll(' ', '')) || null;
    const fullname = this.values['fullname'] && this.values['fullname'].replaceAll('&', 'and');

    if (this.values && cardNumber) {
      this.values = { ...values, cardNumber, fullname };
    }
  }

  canEntry(args: ModalWizardModels.StepValidationArgs) {
    return true;
  }

  canExit(args: ModalWizardModels.StepValidationArgs) {
    const isFormValid = this.form ? this.form.valid : true;

    return isFormValid && this.authorizenetComponent
      ? this.authorizenetComponent
          .dispatchData({
            cardData: { ...(this.values as CardData) },
          })
          .pipe(untilDestroyed(this), tap(this.onDispatchDataSuccess.bind(this)), catchError(this.onDispatchDataFailed.bind(this)))
      : of(isFormValid);
  }

  /**
   * Handle data of successfully done "dispatch data"
   * request and return True if there is data.
   *
   * @param data  Response data.
   * @returns  Boolean
   */
  private onDispatchDataSuccess(data: DispatchDataResponse | null): boolean {
    // Update store with new data
    this.biddingStore.update({
      [BiddingStoreKey.bidDeposit]:
        data?.messages.resultCode === 'Ok'
          ? {
              DATAVALUE: data.opaqueData.dataValue,
              DATADESCRIPTOR: data.opaqueData.dataDescriptor,
            }
          : null,
    });
    return !!data;
  }

  /**
   * Handle failed "dispatch data" request. Return False all the time
   *
   * @param error  Failed response data.
   * @returns Boolean
   */
  private onDispatchDataFailed(error: any): Observable<boolean> {
    this.notification.showToast({
      styleClass: 'p-toast-message-error',
      closable: true,
      summary: 'Error',
      detail: 'Could not withdraw deposit. Please verify your card information and try again.',
    });
    console.error(error?.messages?.message?.[0]?.text);
    return of(false);
  }
}
